/**
 * THE list renderer. Every list in this console goes through this component:
 * the feed, the trades, the faults, the fleet, the raw drawer.
 *
 * Two rules it exists to make structural rather than aspirational:
 *
 *  1. **Never render a DOM node for a row that is not visible** (§6.8 rule 4).
 *     The old dashboard mounted a CSS-hidden duplicate mobile list —
 *     ~0.5 M synchronously constructed nodes on top of a 25 MiB parse — and
 *     that, not the JSON size, is what actually froze the browser.
 *  2. **One rendering path at every viewport.** There is no
 *     `@media (max-width: …) { .desktop-only { display: none } }` duplicate
 *     anywhere in `web/`. The LAYOUT changes at 1100 px by adding a grid
 *     column; the RENDERER does not change, and this file is the only list
 *     implementation that exists.
 *
 * Paging is keyset and driven from the bottom of the *materialised* range,
 * in an effect — never during render.
 */

import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";

export interface VirtualListProps<T> {
  rows: T[];
  /** stable identity; a re-fetch must not re-key a row and remount it */
  rowKey: (row: T, index: number) => string;
  renderRow: (row: T, index: number) => React.ReactNode;
  estimateSize?: number;
  overscan?: number;
  /** called when the materialised window reaches the tail of `rows` */
  onEndReached?: () => void;
  /** rendered under the list: paging state, end-of-range, skipped chip */
  footer?: React.ReactNode;
  emptyLabel?: React.ReactNode;
  className?: string;
  /** Opt-in dynamic row measurement. OFF by default on purpose: a
   *  fixed-height row keeps the materialised window a function of the
   *  viewport alone, which is what makes the DOM-node bound provable rather
   *  than probable. Only lists the server already caps (faults, ≤200) turn
   *  it on, where a truncated evidence card would be worse than a variable
   *  row height. */
  dynamic?: boolean;
  /** test seam: happy-dom reports a 0 px viewport, so the range would be
   *  degenerate. Production never passes this — the real element is measured. */
  initialRect?: { width: number; height: number };
  ariaLabel?: string;
}

export function VirtualList<T>({
  rows,
  rowKey,
  renderRow,
  // Measured against the type scale in tokens.css, not guessed. A stale
  // estimate on a `dynamic` list costs a measure pass per row and makes the
  // scrollbar jump; on a fixed list it silently drifts the scroll offset.
  estimateSize = 104,
  overscan = 6,
  onEndReached,
  footer,
  emptyLabel,
  className = "vlist",
  dynamic = false,
  initialRect,
  ariaLabel,
}: VirtualListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan,
    getItemKey: (i) => rowKey(rows[i]!, i),
    ...(initialRect ? { initialRect } : {}),
  });

  const items = virt.getVirtualItems();
  const lastIndex = items.length ? items[items.length - 1]!.index : -1;

  // Paging in an EFFECT. Calling fetchNextPage from the render body is how a
  // list starts fetching in a loop under React 18's double-invocation.
  useEffect(() => {
    if (!onEndReached) return;
    if (rows.length === 0) return;
    if (lastIndex >= rows.length - 5) onEndReached();
  }, [lastIndex, rows.length, onEndReached]);

  return (
    <div className={className} ref={parentRef} role="feed" aria-label={ariaLabel} data-rows={rows.length}>
      {rows.length === 0 && emptyLabel !== undefined && <div className="vlist__empty muted">{emptyLabel}</div>}
      <div className="vlist__sizer" style={{ height: virt.getTotalSize(), position: "relative" }}>
        {items.map((v) => (
          <div
            key={v.key}
            data-index={v.index}
            className="vlist__row"
            ref={dynamic ? virt.measureElement : undefined}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${v.start}px)`,
            }}
          >
            {renderRow(rows[v.index]!, v.index)}
          </div>
        ))}
      </div>
      {footer}
    </div>
  );
}
